import React, { useEffect } from 'react';
import generator from 'generate-password';
import { debounce, isEqual } from 'lodash';
import moment from 'moment';
import { styled } from '@mui/material';
import Papa from 'papaparse';
import { Fragment, Node, Slice } from '@tiptap/pm/model';

import { Divider, Icon, Stack, Text } from 'shell/components';
import NickelDesktopLeftPanel from 'utils/assetFiles/svgs/NickelDesktopLeftPanel';
import NickelDesktopRightPanel from 'utils/assetFiles/svgs/NickelDesktopRightPanel';
import NickelDesktopStep2 from 'utils/assetFiles/svgs/NickelDesktopStep2';
import NickelDesktopStep3 from 'utils/assetFiles/svgs/NickelDesktopStep3';
import NickelDesktopStep4 from 'utils/assetFiles/svgs/NickelDesktopStep4';
import NickelTabletLeftPanel from 'utils/assetFiles/svgs/NickelTabeltLeftPanel';
import NickelTabletRightPanel from 'utils/assetFiles/svgs/NickelTabletBottomPanel';
import NickelTabletStep2 from 'utils/assetFiles/svgs/NickelTabletStep2';
import NickelTabletStep3 from 'utils/assetFiles/svgs/NickelTabletStep3';
import NickelTabletStep4 from 'utils/assetFiles/svgs/NickelTabletStep4';
import NickelMobileTopPanel from 'utils/assetFiles/svgs/NickelMobileTopPanel';
import NickelMobileStep2 from 'utils/assetFiles/svgs/NickelMobileStep2';
import NickelMobileStep3 from 'utils/assetFiles/svgs/NickelMobileStep3';
import NickelMobileStep4 from 'utils/assetFiles/svgs/NickelMobileStep4';
import {
    COUNTRY_LIST,
    EN_PROFICIENCY_OPTIONS,
    PRONOUN_OPTIONS,
    TASK_STATUS_OPTIONS,
    TASK_WORKLOAD_OPTIONS,
    TIME_ZONE_LIST,
    TYPES,
    WORKLOAD_POINTS,
} from 'utils/constants';

export function GetObjectById(array = []) {
    const obj = {};
    array.forEach((item) => {
        obj[item.id] = item;
    });
    return obj;
}

export function splitArrayByCondition(arr, conditionCallback) {
    const result = {
        true: [],
        false: [],
    };
    arr.forEach((obj) => {
        const condition = conditionCallback(obj);
        result[condition].push(obj);
    });
    return result;
}

export function uppercaseFirstLetter(string = '') {
    return string.charAt(0).toUpperCase() + string.slice(1);
}

export function DownloadFileFromURL(url, name) {
    const anchor = document.createElement('a');
    anchor.style.display = 'none';
    anchor.href = url;
    anchor.download = name;
    anchor.target = '_blank';

    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
}

export function truncateText(text = '', maxNumber = 16) {
    if (text.length > maxNumber) {
        return `${text.substring(0, maxNumber)}...`;
    }
    return text;
}

export const useOutsideClick = (ref, callback) => {
    const handleClick = (e) => {
        if (ref.current && !ref.current.contains(e.target)) {
            callback();
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClick);
        return () => {
            document.removeEventListener('mousedown', handleClick);
        };
    });
};

export const getTextWidth = (text, fontSize) => {
    var canvas = getTextWidth.canvas || (getTextWidth.canvas = document.createElement('canvas'));
    var context = canvas.getContext('2d');
    context.font = `${fontSize}px Sofia Pro`;
    var metrics = context.measureText(text);
    return metrics.width;
};

export const getProperTextWidth = (text = '', maxWidth, fontSize) => {
    if (text.length <= 0) return text;
    let tempText = truncateText(text, 30);
    let maxCharacters = 30;
    while (true) {
        const textLength = getTextWidth(tempText + '...', fontSize || 14);
        if (textLength > maxWidth) tempText = truncateText(text, maxCharacters);
        else break;
        maxCharacters--;
    }

    return tempText;
};

export const getUserPreferredFullName = (data) => {
    const { first_name = '', last_name = '', display_name = '', email = '' } = data || {};

    if (display_name) {
        return `${display_name || ''} ${last_name || ''}`.trim();
    }

    return `${first_name || ''} ${last_name || ''}`.trim() || email;
};

export const getUserPronoun = (userGender) => {
    const pronounOption = PRONOUN_OPTIONS.find(({ value }) => value === userGender);
    if (!pronounOption) return '';
    return pronounOption.title;
};

export const getUserCountry = (userCountry) => {
    const countryOption = COUNTRY_LIST.find(({ code }) => code === userCountry);
    if (!countryOption) return '';
    return countryOption.name;
};

export const getTaskStatus = (taskStatus) => {
    return TASK_STATUS_OPTIONS.find(({ value }) => value === taskStatus);
};

export const getTaskWorkload = (taskWorkload) => {
    return TASK_WORKLOAD_OPTIONS.find(({ value }) => value === taskWorkload);
};

export const getFileIconKind = (file) => {
    switch (file.type) {
        case 'application/pdf':
            return 'pdfType';
        case 'application/zip':
            return 'zipType';
        case 'application/msword':
        case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
            return 'docType';
        case 'image/gif':
            return 'gifType';
        case 'image/jpeg':
            return 'jpgType';
        case 'image/png':
            return 'pngType';
        case 'image/svg+xml':
            return 'svgType';
        case 'text/plain':
            return 'txtType';
        default:
            return 'attach';
    }
};

export const getPrettyMIMEType = (mimeType) => {
    switch (mimeType) {
        case 'application/pdf':
            return 'PDF';
        case 'application/msword':
        case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
            return 'Word';
        case 'application/vnd.ms-excel':
        case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
            return 'Excel';
        case 'application/vnd.ms-powerpoint':
        case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
            return 'Powerpoint';
        case 'application/zip':
            return 'ZIP';
        case 'image/gif':
        case 'image/jpeg':
        case 'image/png':
            return 'Image';
        default:
            return '';
    }
};

export const getFileNameExt = (name) => {
    return name.split('.').pop();
};

export const getResourceName = (resource) => {
    return resource.data?.name || resource.name;
};

export const getS3FileURL = (data) => {
    if (!data) return;
    const { bucket, key, name } = data;
    if (!bucket || !key || !name) return '';
    return `https://${bucket}.s3.amazonaws.com/${key}/${encodeURIComponent(name)}`;
};

// si - true to use metric (SI) units, aka powers of 1000, false to use binary (IEC), aka powers of 1024
// dp - number of decimal places to display
export const getPrettyFileSize = (bytes, si = false, dp = 1) => {
    const thresh = si ? 1000 : 1024;

    if (Math.abs(bytes) < thresh) {
        return bytes + ' B';
    }

    const units = si
        ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
        : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
    let u = -1;
    const r = 10 ** dp;

    do {
        bytes /= thresh;
        ++u;
    } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);

    return bytes.toFixed(dp) + ' ' + units[u];
};

export const downloadFileFromURL = (url, name) => {
    const anchor = document.createElement('a');
    anchor.style.display = 'none';
    anchor.href = url;
    anchor.download = name;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';

    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
};

export const getAbsoluteURL = (url) => {
    return ['http://', 'https://'].some((prefix) => url.startsWith(prefix)) ? url : `https://${url}`;
};

export const getPrettyTimestamp = (timestamp) => {
    const getAweekAgo = new Date().getTime() - 604800000;
    if (timestamp < getAweekAgo) return moment(timestamp).format('ll');
    else return moment(timestamp).startOf(new Date()).fromNow();
};

export const debounceRequest = debounce((requestFunc) => {
    requestFunc();
}, 1000);

export const generatePassword = () => {
    return generator.generate({
        length: 10,
        numbers: true,
    });
};

export const getTemplateStatus = (template) => {
    const now = new Date().getTime();
    if (!template.published_on) return 'Saved as Draft';
    if (template.published_on < now) return 'Published';
    return `Scheduled for ${moment(template.published_on).format('MMMM D[,] YYYY')}`;
};

export const getTemplateStatusWithIcon = (template) => {
    const now = new Date().getTime();
    if (!template.published_on)
        return (
            <>
                <Icon style={{ margin: '0 8px -3px 0', width: 16, height: 16 }} kind={'save'} />
                Saved as Draft
            </>
        );
    if (template.published_on < now)
        return (
            <>
                <Icon style={{ margin: '0 8px -3px 0', width: 16, height: 16 }} kind={'fileUploaded'} />
                Published
            </>
        );
    return (
        <>
            <Icon style={{ margin: '0 8px -3px 0', width: 16, height: 16 }} kind={'calendar'} />
            Scheduled for {moment(template.published_on).format('MMMM D[,] YYYY')}
        </>
    );
};

export const getDueDate = (template) => {
    return moment(template.end_on).format('MMMM D[,] YYYY');
};

export const getTemplateCompletion = (spawnedTasks) => (template) => {
    if (getTemplateStatus(template) === 'Published') {
        return `${Object.entries(spawnedTasks).reduce(
            (acc, [taskId, task]) =>
                task ? (template.task_ids.includes(taskId) && task.status === 'completed' ? acc + 1 : acc) : acc,
            0
        )} / ${template.task_ids.length}`;
    }
    return '--';
};

export const getTemplateCompletionFraction = (spawnedTasks) => (template) => {
    if (getTemplateStatus(template) !== 'Published') return 0;
    return (
        Object.entries(spawnedTasks).reduce(
            (acc, [taskId, task]) =>
                task ? (template.task_ids.includes(taskId) && task.status === 'completed' ? acc + 1 : acc) : acc,
            0
        ) / template.task_ids.length
    );
};

export const getMilestoneStatus = (milestone) => getTemplateStatus(milestone);

export const getMilestoneCompletion = (spawnedTasks) => (milestone) => {
    const tasks = Object.values(spawnedTasks)
        .filter((task) => task)
        .filter((task) => task.type === TYPES.MILESTONE && task.milestone_id === milestone.id);
    if (getTemplateStatus(milestone) !== 'Published' || !tasks.length) return '--';
    const completedTasks = tasks.filter((task) => task.status === 'completed');
    return `${completedTasks.length}/${tasks.length}`;
};

export const insert = (arr, index, newItem) => [...arr.slice(0, index), newItem, ...arr.slice(index)];

// temporary handleKeyPress callback to implement keyboard accessibility
export const handleKeyPress = (callback) => (event) => {
    if ([' ', 'Enter'].includes(event.key)) {
        event.preventDefault();
        event.stopPropagation();
        callback();
    }
};

export const findDefaultProfile = (type, user) => {
    switch (type) {
        case 'country': {
            const findCountry = COUNTRY_LIST.find(({ code }) => code === user.country);
            return findCountry ? { key: findCountry.code, value: findCountry.name } : null;
        }

        case 'time-zone': {
            const findTimeZone = TIME_ZONE_LIST.find(({ text }) => text === user.time_zone);
            return findTimeZone ? { key: findTimeZone.text, value: findTimeZone.text } : null;
        }

        case 'gender': {
            const findGender = PRONOUN_OPTIONS.find(({ value }) => value === user.gender);

            return findGender ? { key: findGender.value, value: findGender.title } : null;
        }

        case 'english-proficiency': {
            const findEnProficiency = EN_PROFICIENCY_OPTIONS.find(({ value }) => value === user.english_proficiency);

            return findEnProficiency ? { key: findEnProficiency.value, value: findEnProficiency.title } : null;
        }

        default:
            return null;
    }
};

export const polarToCartesian = (centerX, centerY, radius, angleIntoDegrees) => {
    const angleInRadians = ((angleIntoDegrees - 90) * Math.PI) / 180.0;

    return {
        x: centerX + radius * Math.cos(angleInRadians),
        y: centerY + radius * Math.sin(angleInRadians),
    };
};

export const findCenter = (radius, strokeWidth, startAngle) => {
    const start = polarToCartesian(radius, radius, radius - strokeWidth / 1.5, startAngle);

    return {
        x: start.x,
        y: start.y,
    };
};

const CustomPath = styled('path')(({ onClick, theme }) => {
    return {
        padding: 0.5,
        ...(onClick && {
            '&:hover': {
                stroke: theme.palette.ensightful.primary[500],
            },
        }),
    };
});

export const DrawArc = ({ radius, startAngle, endAngle, strokeColor, strokeWidth, onClick }) => {
    const center = radius;
    const start = polarToCartesian(center, center, radius, startAngle);
    const end = polarToCartesian(center, center, radius, endAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
    let d;

    if (endAngle === 359) {
        d = ['M', start.x, start.y, 'A', radius, radius, 0, largeArcFlag, 1, end.x, end.y, 'Z'].join(' ');
    } else {
        d = ['M', start.x, start.y, 'A', radius, radius, 0, largeArcFlag, 1, end.x, end.y].join(' ');
    }

    return (
        <CustomPath
            cursor={onClick ? 'pointer' : 'default'}
            d={d}
            fill={'none'}
            stroke={strokeColor}
            strokeLinecap={'butt'}
            strokeWidth={strokeWidth}
            onClick={onClick}
        />
    );
};

const getTotalPts = (distribution) => {
    return Object.entries(distribution).reduce((acc, cur) => {
        const [, { unassigned = null }] = cur;
        const { completed = 0, total = 0 } = unassigned?.points;
        const progress = total ? Math.ceil((completed * 100) / total) : 0;
        return acc + progress;
    }, 0);
};

export const getAverage = (distribution, groups) => {
    return groups.length ? (getTotalPts(distribution) / groups.length).toFixed(2) : 0;
};

export const getProgress = (distribution, groupId) => {
    const completedPoints = distribution[groupId]?.unassigned?.points?.completed;
    const totalPoints = distribution[groupId]?.unassigned?.points?.total;
    const progress = !totalPoints ? 0 : ((completedPoints / totalPoints) * 100).toFixed();
    return progress;
};

export const getStatus = (distribution, groups, groupId) => {
    if (!distribution || !groups || !groupId) return 'onTrack';
    const getDiffSum = () => {
        const average = getAverage(distribution, groups);
        const diffSum = Object.entries(distribution).reduce((acc, cur) => {
            const [, { unassigned = null }] = cur;
            const { completed = 0, total = 0 } = unassigned?.points || {};
            const progress = total ? Math.ceil((completed * 100) / total) : 0;
            return acc + Math.pow(progress - average, 2);
        }, 0);
        return diffSum;
    };

    const getDeviation = () => {
        const diffSum = getDiffSum();
        const sumAvg = groups.length ? diffSum / groups.length : 0;
        const deviation = Math.sqrt(sumAvg);
        return deviation;
    };

    const getPosition = (groupId) => {
        const deviation = getDeviation();
        const average = getAverage(distribution, groups);

        const { points } = distribution[groupId]?.unassigned || {};
        const { completed = 0, total = 0 } = points || {};
        const progress = total ? Math.ceil((completed / total) * 100) : 0;
        const position = deviation > 0 ? (progress - average) / deviation : 0;
        return position;
    };

    const variance = getPosition(groupId);
    if (variance < -1) return 'offTrack';
    else if (variance < 0) return 'caution';
    else return 'onTrack';
};

export const downloadCSV = (filename, csv) => {
    const unparsed = Papa.unparse(csv);
    const blob = new Blob([unparsed], { type: 'text/plain;charset=utf-8' });
    return downloadFileFromURL(URL.createObjectURL(blob), filename);
};

export const parseCSV = (file, expectedHeaders, message) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const text = reader.result;
            const parsed = Papa.parse(text);
            if (parsed.errors.length) {
                if (message) message.error('The CSV file could not be read. Please make sure that your file is valid.');
                reject(parsed.errors);
            }

            // remove last row if it is just an empty cell, this happens when there is a trailing newline
            const lastRow = parsed.data[parsed.data.length - 1];
            if (lastRow.length === 1 && lastRow[0] === '') {
                parsed.data = parsed.data.slice(0, parsed.data.length - 1);
            }

            const [header, ...rest] = parsed.data.map((row) => row.map((cell) => cell.trim()));
            if (message && !isEqual(header, expectedHeaders)) {
                message.error(
                    'We ran into an error reading your file. Please make sure the column headers match the CSV template.'
                );
            }
            resolve(rest);
        };
        if (file) reader.readAsText(file);
    });
};

export const stripHTML = (html) => {
    if (!html) return '';
    return (html.match(/<p>(.*?)<\/p>/g) || [])
        .map((segment) => {
            const tempEl = document.createElement('div');
            tempEl.innerHTML = segment;
            return tempEl.textContent || tempEl.innerText || '';
        })
        .join('\n');
};

// RTE handler for pasting
export const clipboardTextParser = (text, context) => {
    const blocks = text.replace().split(/(?:\r\n?|\n)/);
    const nodes = [];
    blocks.forEach((line) => {
        const nodeJSON = { type: 'paragraph' };
        if (line.length) {
            nodeJSON.content = [{ type: 'text', text: line }];
        }
        const node = Node.fromJSON(context.doc.type.schema, nodeJSON);
        nodes.push(node);
    });
    return Slice.maxOpen(Fragment.fromArray(nodes));
};

export const getTooltipComponent = (title, description) => {
    return (
        <Stack divider={<Divider />} spacing={1}>
            <Text color={'inherit'} variant={'p3'} weight={'semibold'}>
                {title}
            </Text>
            <Stack>
                <Text color={'inherit'} variant={'p3'}>
                    {description}
                </Text>
            </Stack>
        </Stack>
    );
};

export const getTooltipProps = (tooltip) => {
    const { component, maxWidth, placement, ...rest } = tooltip;
    return {
        title: component,
        maxWidth: maxWidth || '8.75rem',
        enterDelay: 300,
        placement: placement || 'bottom',
        ...rest,
    };
};

export const COURSE_ID_DICTIONARY = [
    '0',
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9',
    'a',
    'b',
    'c',
    'd',
    'e',
    'f',
    'g',
    'h',
    'i',
    'j',
    'k',
    'l',
    'm',
    'n',
    'o',
    'p',
    'q',
    'r',
    's',
    't',
    'u',
    'v',
    'w',
    'x',
    'y',
    'z',
];

export const insertAtIndex = (arr, start, ...items) => {
    return [...arr.slice(0, start), ...items, ...arr.slice(start)];
};

export const getHTMLWordCount = (html) => {
    const stripped = stripHTML(html).trim();
    if (!stripped.length) return 0;
    return stripped.split(' ').length;
};

export const getHTMLCharacterCount = (html) => {
    const stripped = stripHTML(html).trim();
    if (!stripped.length) return 0;
    return stripped.length;
};

export const buildPrompt = (studentName, formative, submission) => {
    let prompt = '';
    prompt += `Imagine you are my teaching assistant. I want your help in giving my students some feedback on their drafts. First, here is some context around the assignment.\n\n`;

    prompt += `${stripHTML(formative.description)}\n\n`;

    prompt += `This is the rubric, which I am using to grade:\n\n`;

    formative.rubric.forEach((criterion, index) => {
        prompt += `Criterion ${index + 1}: ${criterion.title}\n`;
        criterion.options.forEach((option) => {
            prompt += `${option.rate} pts: ${option.title}: ${option.description}\n`;
        });
        prompt += '\n';
    });

    if (formative.baseline[0].sample_submission) {
        const total = formative.rubric.reduce((acc, cur) => {
            return acc + Math.max(...cur.options.map(({ rate }) => parseInt(rate)));
        }, 0);
        const scoreSum = Object.entries(formative.baseline[0].grading)
            .map(([criterionId, optionId]) => {
                return parseInt(
                    formative.rubric.find(({ id }) => id === criterionId).options.find(({ id }) => id === optionId).rate
                );
            })
            .reduce((acc, cur) => acc + cur, 0);

        prompt += `This is a baseline paper that I gave a ${scoreSum}/${total} for you to understand what I am looking for.\n\n`;

        prompt += `START OF BASELINE\n\n`;

        prompt += `${stripHTML(formative.baseline[0].sample_submission)}\n\n`;

        prompt += `END OF BASELINE\n\n`;
    }

    prompt += `START OF STUDENT SUBMISSION\n\n`;

    prompt += `${stripHTML(submission)}\n\n`;

    prompt += `END OF STUDENT SUBMISSION\n\n`;

    prompt += `Here are your tasks: Using the rubric, assignment details, and the baseline I have shared with you, provide feedback on the student's submission. Start with commentary about the student's submission and then provide suggestions with clear examples. If the students write gibberish, please restate the assignment requirements.\n\n`;

    if (formative.custom_context) {
        prompt += `Some additional things to look out for:\n\n`;

        prompt += `${stripHTML(formative.custom_context)}\n\n`;
    }

    prompt += `Here are some things that you should not do: Do not add any sign offs at the end of your response. Do not mention the baseline in your response. Do not include any grading or points in your response.\n\n`;

    if (studentName) {
        prompt += `Address the student directly by their first name. Their full name is ${studentName}.\n\n`;
    } else {
        prompt += 'Start your response with "Hello there".\n\n';
    }

    prompt += `Your response should start with a short paragraph of commentary. Then, continue with 5 suggestions based on the context that I have given you with 2 bullet points for each suggestion.`;

    return prompt;
};

export const getChatCompletion = async (messages) => {
    const url = process.env.REACT_APP_OPEN_AI_API;
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    return await fetch(url, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            model: 'gpt-4',
            messages,
            stream: true,
            max_tokens: 1000,
        }),
    });
};

export const NICKEL_TOUR_RESOURCES = {
    desktop: {
        0: {
            left: NickelDesktopLeftPanel,
            right: NickelDesktopRightPanel,
        },
        1: {
            left: null,
            right: NickelDesktopStep2,
        },
        2: {
            left: null,
            right: NickelDesktopStep3,
        },
        3: {
            left: null,
            right: NickelDesktopStep4,
        },
    },
    tablet: {
        0: {
            left: NickelTabletLeftPanel,
            right: NickelTabletRightPanel,
        },
        1: {
            left: null,
            right: NickelTabletStep2,
        },
        2: {
            left: null,
            right: NickelTabletStep3,
        },
        3: {
            left: null,
            right: NickelTabletStep4,
        },
    },
    mobile: {
        0: {
            left: null,
            right: NickelMobileTopPanel,
        },
        1: {
            left: null,
            right: NickelMobileStep2,
        },
        2: {
            left: null,
            right: NickelMobileStep3,
        },
        3: {
            left: null,
            right: NickelMobileStep4,
        },
    },
};

// completedPoints is how many points the given ownerAccountId has completed
// totalPoints is how many points the given ownerAccountId is contributing to
// percentage is how many percent the given ownerAccountId has completed
export const getAnalyticsStats = (tasks, ownerAccountId) => {
    let completedPoints = 0;
    let totalPoints = 0;

    if (ownerAccountId) {
        tasks
            .filter((task) => {
                return task.collaborator_account_ids?.length && task.collaborator_account_ids.includes(ownerAccountId);
            })
            .forEach((task) => {
                const contribution = WORKLOAD_POINTS[task.workload] / task.collaborator_account_ids.length;
                totalPoints += contribution;
                if (task.status === 'completed') completedPoints += contribution;
            });
    } else {
        tasks
            .filter((task) => {
                return !task.collaborator_account_ids?.length;
            })
            .forEach((task) => {
                totalPoints += WORKLOAD_POINTS[task.workload];
                if (task.status === 'completed') completedPoints += WORKLOAD_POINTS[task.workload];
            });
    }

    const percentage = totalPoints ? (completedPoints / totalPoints) * 100 : 0;

    return {
        completedPoints: Number(completedPoints.toFixed(2)),
        totalPoints: Number(totalPoints.toFixed(2)),
        percentage,
    };
};

// given a value, return true if the value is a natural number
// natural numbers are non-negative integers
export const isNatural = (value) => {
    const parsed = parseInt(value);
    return !isNaN(parsed) && Number.isInteger(parsed) && parsed > -1;
};

export const getDuplicateTitle = (originalTitle) => {
    return `Copy of ${originalTitle}`;
};
